/* New-chart wizard (W2-14 onboarding-wizard). Design tokens only (src/ui/styles/tokens.css). */

.stepper {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 0 0 var(--space-4);
  padding: 0;
  list-style: none;
}

.stepItem {
  display: flex;
}

.stepButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--control-h-md);
  padding: 0 var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.stepButton:disabled {
  cursor: default;
}

.stepButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.stepItem[data-state='current'] .stepButton {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
  color: var(--color-text);
  font-weight: var(--weight-semibold);
}

.stepItem[data-state='done'] .stepButton {
  color: var(--color-text);
}

.stepItem[data-state='skipped'] .stepButton {
  border-style: dashed;
}

.stepNumber {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(18px * var(--ui-scale));
  height: calc(18px * var(--ui-scale));
  border-radius: var(--radius-pill);
  background: var(--color-surface-3);
  font-size: var(--text-xs);
}

.stepItem[data-state='current'] .stepNumber {
  background: var(--color-accent);
  color: var(--color-text-on-accent);
}

.stepItem[data-state='done'] .stepNumber {
  background: var(--color-success-muted);
  color: var(--color-success);
}

.stepTitle {
  white-space: nowrap;
}

.stepBody {
  /* One height for every step (the tallest, Beat, fits): Next and Back stay put (UX-085). */
  min-height: min(calc(560px * var(--ui-scale)), 62vh);
}

.footer {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
}

.footerSpacer {
  flex: 1;
}

.step {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.lead {
  margin: 0;
  font-size: var(--text-lg);
}

.hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-danger-muted);
  color: var(--color-text);
}

.error > svg {
  flex: none;
  color: var(--color-danger);
}

.hiddenInput {
  display: none;
}

/* ---------- drop zones ---------- */

.dropZone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-6) var(--space-5);
  border: 2px dashed var(--color-border-strong);
  border-radius: var(--radius-lg);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  text-align: center;
  transition: border-color var(--duration-fast) var(--ease-standard);
}

.dropZone[data-over='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
}

/* A file is chosen: the zone stays a drop target for another one, in one short row (room for the previews).
   'short': the same row on short screens only (see the max-height media query below). */
.dropZone[data-compact='true'] {
  flex-flow: row wrap;
  justify-content: center;
  gap: var(--space-2) var(--space-4);
  padding: var(--space-3) var(--space-4);
}

.dropText {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin: 0;
  color: var(--color-text);
}

/* ---------- song ---------- */

.songCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
}

.songHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.songName {
  font-weight: var(--weight-semibold);
  overflow-wrap: anywhere;
}

.songFacts {
  flex: 1;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.waveRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.waveRow > .waveform {
  flex: 1 1 0;
  min-width: 0;
}

/* Fixed height everywhere: in a column (the Details step) a growing canvas would feed its own size back. */
.waveform {
  display: block;
  flex: none;
  width: 100%;
  height: calc(56px * var(--ui-scale));
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-curve);
}

.waveform[data-clickable='true'] {
  cursor: pointer;
}

.verdict {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
}

.verdict > svg {
  flex: none;
  margin-top: 2px;
}

.verdict[data-kind='ok'] > svg {
  color: var(--color-success);
}

.verdict[data-kind='info'] > svg {
  color: var(--color-info);
}

.verdict[data-kind='warning'] > svg {
  color: var(--color-warning);
}

.verdictBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.detect {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 1.2em;
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* ---------- picture ---------- */

.previews {
  --wizard-preview-chrome: calc(380px * var(--ui-scale));

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(220px * var(--ui-scale)), 1fr));
  gap: var(--space-4);
}

.previewFigure {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
}

.previewCanvas {
  display: block;
  /* On short screens (1280 x 600) both previews and their captions stay on screen with the dialog's chrome. */
  width: min(100%, calc((100dvh - var(--wizard-preview-chrome)) * 16 / 9));
  aspect-ratio: 16 / 9;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text);
}

/* ---------- details ---------- */

.fieldGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(240px * var(--ui-scale)), 1fr));
  gap: var(--space-3) var(--space-5);
}

.fieldRow {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.fieldNote {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.fieldNote[data-kind='warning'] > svg {
  color: var(--color-warning);
}

.group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0;
  padding: var(--space-3) var(--space-4) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
}

.group > legend {
  padding: 0 var(--space-2);
  font-weight: var(--weight-semibold);
}

.difficultyRow,
.previewRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-4);
}

/* "Shown as ..." beside the level controls (it wraps below them when the row is narrow). */
.levelPreview {
  align-self: center;
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

/* ---------- start ---------- */

.layouts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(160px * var(--ui-scale)), 1fr));
  gap: var(--space-3);
}

.layoutCard {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
  color: var(--color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.layoutCard:hover {
  background: var(--color-surface-hover);
}

.layoutCard:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.layoutCard[data-checked='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
}

.layoutTitle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-weight: var(--weight-semibold);
}

.layoutTitle > svg {
  color: var(--color-accent);
}

.layoutBody {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.layoutPicture {
  width: 100%;
  height: auto;
}

.layoutFrame {
  fill: var(--color-surface-sunken);
  stroke: var(--color-border-strong);
}

.layoutLine {
  stroke: var(--color-text);
  stroke-width: 2;
  stroke-linecap: round;
}

.layoutLineFaint {
  stroke: var(--color-text-subtle);
  stroke-width: 1;
  stroke-dasharray: 3 3;
}

.layoutPlus line {
  stroke: var(--color-text-subtle);
  stroke-width: 2;
  stroke-linecap: round;
}

.layoutNote {
  fill: var(--color-note-tap);
  animation: noteFall 2.4s linear infinite;
}

@keyframes noteFall {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  70% {
    transform: translateY(66px);
    opacity: 1;
  }
  80%,
  100% {
    transform: translateY(66px);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  :root:not([data-reduced-motion='false']) .layoutNote {
    animation: none;
    transform: translateY(40px);
  }
}

:root[data-reduced-motion='true'] .layoutNote {
  animation: none;
  transform: translateY(40px);
}

.summary {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
}

.summaryTitle {
  margin: 0;
  font-size: var(--text-md);
}

.summaryList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.summaryList > li {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.reminders {
  padding: var(--space-3);
  border-left: 3px solid var(--color-severity-tip);
  border-radius: var(--radius-sm);
  background: var(--color-severity-tip-muted);
}

.reminders ul {
  margin: var(--space-1) 0 0;
  padding-left: var(--space-5);
}

.remindTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-weight: var(--weight-medium);
}

/* Short screens (laptops at 1280 x 600, 1366 x 650): tighter spacing, smaller layout pictures and a compact
   picture drop zone, so every step fits without scrolling the dialog. Last in the file: it overrides the rules
   above. */
@media (max-height: 700px) {
  .step {
    gap: var(--space-3);
  }

  .stepper {
    margin-bottom: var(--space-3);
  }

  .group {
    padding: var(--space-2) var(--space-4) var(--space-3);
  }

  /* The Picture step before a picture is chosen: one row, so both previews and their captions stay on screen. */
  .dropZone[data-compact='short'] {
    flex-flow: row wrap;
    justify-content: center;
    gap: var(--space-2) var(--space-4);
    padding: var(--space-3) var(--space-4);
  }

  .layoutPicture {
    max-height: calc(60px * var(--ui-scale));
  }

  /* Start step: the summary facts share one wrapping row, so the reminders and the persistent-storage sentence
     stay on screen on the all-skipped first-project path. */
  .lead {
    font-size: var(--text-md);
  }

  .summary {
    padding: var(--space-3) var(--space-4);
  }

  .summaryList {
    flex-flow: row wrap;
    gap: var(--space-1) var(--space-4);
  }

  .reminders {
    padding: var(--space-2) var(--space-3);
  }
}
